import { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Input, Select, Loading, Heading, Page, Tooltip, Card, Checkbox } from '@aidkitorg/component-library';
import { usePost } from '../API';
import { useLocalizedStrings } from '../Localization';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { ThreeColumnPage } from '../Components/ThreeColumnPage';
import { type User } from '@aidkitorg/aidkit/lib/users/UserService';
import { type UserGroup } from '@aidkitorg/aidkit/lib/users/UserGroupService';
import { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';
import { emailValidator } from '../utils/emailValidator';
import { phoneValidator } from '../utils/phoneValidator';
import { isUserHiddenFromNonAdmins } from './UserManagement';

function UserPageInner() {
  const { uid } = useParams<{ uid: string }>();
  const getUser = usePost('/user/get');
  const updateUser = usePost('/user/update');
  const deleteUser = usePost('/user/delete');
  const getUserGroups = usePost('/user-group/list');
  const getUserGroup = usePost('/user-group/get');
  const getOrgs = usePost('/organization/list');
  const getEditableTags = usePost('/tags/list')
  const L = useLocalizedStrings();
  const history = useHistory();

  const [user, setUser] = useState<User>();
  const [draft, setDraft] = useState<Partial<User>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignableUserGroups, setAssignableUserGroups] = useState<UserGroup[]>([]);
  const [currentUserGroup, setCurrentUserGroup] = useState<UserGroup>();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [editing, setEditing] = useState(false);
  const [editableTags, setEditableTags] = useState<string[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hiddenFromNonAdmins, setHiddenFromNonAdmins] = useState(false);
  const [formValid, setFormValid] = useState({
    name: false,
    email: false,
    phone: false
  });

  useEffect(() => {
    if (!user || user?.source !== 'User Management') {
      // if the user is not loaded or is not from User Management, they cannot edit.
      setCanEdit(false);
    } else if (user && !user.user_group) {
      // if the user has no group, they can assign one.
      setCanEdit(true);
    } else if (user && user.user_group) {
      // if the user has a group, check if they can edit it.
      setCanEdit(!!currentUserGroup?.canEdit);
    }
  }, [user, currentUserGroup]);

  useAsyncEffect(async () => {
    setLoading(true);
    const [userRes, orgsRes, editableTagsRes] = await Promise.all([
      getUser({ uid }),
      getOrgs({}),
      getEditableTags({})
    ]);
    setUser(userRes);
    setDraft(userRes);
    setEditableTags(editableTagsRes);
    setIsAdmin(Array.isArray(editableTagsRes) && editableTagsRes.includes('admin'));
    setHiddenFromNonAdmins(isUserHiddenFromNonAdmins(userRes));
    setOrgs(Array.isArray(orgsRes) ? orgsRes : []);
    if (userRes.organization) {
      // only show user groups for the user's organization that they can edit.
      const userGroupRes = await getUserGroups({ organization: userRes.organization });
      if (Array.isArray(userGroupRes)) {
        setAssignableUserGroups(userGroupRes.filter(g => g.canEdit));
      } 
    }
    if (userRes.user_group) {
      const userGroupRes = await getUserGroup({ id: userRes.user_group });
      if (userGroupRes) {
        setCurrentUserGroup(userGroupRes);
      }
    } else {
      setCurrentUserGroup(undefined);
    }
    setLoading(false);
  }, [uid]);

  useAsyncEffect(async () => {
    if (draft?.organization) {
      // only show user groups for the user's organization
      const userGroupRes = await getUserGroups({ organization: draft.organization });
      if (Array.isArray(userGroupRes)) {
        setAssignableUserGroups(userGroupRes.filter(g => g.canEdit));
      }
    }
  }, [draft?.organization]);

  const handleSave = async () => {
    setSaving(true);
    
    // Prepare metadata with hidden state (only for admins)
    let metadata = {};
    try {
      metadata = user?.metadata ? JSON.parse(user.metadata) : {};
    } catch (e) {
      metadata = {};
    }
    
    if (isAdmin) {
      metadata = {
        ...metadata,
        HiddenFromNonAdmins: hiddenFromNonAdmins,
        last_updated_at: new Date().toISOString(),
        last_updated_by: 'user-interface'
      };
    }
    
    const updateRes = await updateUser({ 
      ...draft, 
      uid,
      metadata: JSON.stringify(metadata)
    });
    
    if (updateRes.uid) {
      setUser(updateRes);
      setHiddenFromNonAdmins(isUserHiddenFromNonAdmins(updateRes));
      setEditing(false);
    } else {
      setEditing(false);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (window.confirm(L.user.confirm_delete_user)) {
      await deleteUser({ uid });
      history.push('/users');
    }
  };

  const handleCancel = () => {
    setDraft(user || {});
    setHiddenFromNonAdmins(user ? isUserHiddenFromNonAdmins(user) : false);
    setEditing(false);
  };

  useEffect(() => {
    if (!draft) return;
    setFormValid({
      name: (draft.name ?? '').length > 0,
      email: emailValidator(draft.email ?? ''),
      phone: !draft.phone || phoneValidator(draft.phone),
    });
  }, [draft]);

  const userGroupOptions = useMemo(() => {
    return assignableUserGroups.filter(g => {
      const groupPerms = g.permissions.split(',').filter(Boolean);
      if (editableTags.includes('admin') || !groupPerms.some(tag => !editableTags.includes(tag))) {
        return true;
      }
      return false;
    }).map(g => ({ label: g.name, value: g.id.toString() }));
  }, [editableTags, assignableUserGroups]);

  if (loading) return <Loading labels={[{ content: L.user.fetching_user }]} />;

  return <div className="pt-4">
    <Heading>{L.user.user_details}</Heading>
    <Card>
      {editing ? <form
        onSubmit={e => {
          e.preventDefault();
          handleSave();
        }}
        className="space-y-4"
      >
        <Input
          label={L.user.name}
          value={draft.name || ''}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          error={{hasError: !formValid.name, errorMessage : L.questions.textentry.this_field_is_required}}
        />
        <Input
          label={L.user.email}
          value={draft.email || ''}
          onChange={e => setDraft({ ...draft, email: e.target.value })}
          error={{hasError: !formValid.email, errorMessage : L.questions.textentry.must_be_a_valid_email}}
        />
        <Input
          label={L.user.phone}
          value={draft.phone || ''}
          onChange={e => setDraft({ ...draft, phone: e.target.value })}
          error={{hasError: !formValid.phone, errorMessage : L.user.must_be_a_valid_phone_number}}
        />
        <Input
          label={L.user.organization}
          value={orgs.find(org => org.uid === draft.organization)?.name || ''}
          disabled
        />
        <Select
          label={L.user.user_group}
          options={userGroupOptions}
          value={{ value: draft.user_group?.toString() || '', label: draft.user_group_name || '' }}
          onChange={option => {
            setDraft({ ...draft, user_group: parseInt(option.value), user_group_name: assignableUserGroups.find(g => g.id === parseInt(option.value))?.name || '' });
          }}
          description={L.user.user_group}
          labelLocation="above"
            
        />
        {isAdmin && (
          <Checkbox
            label={L.user.hidden}
            description={L.user.hidden_from_non_admins}
            checked={hiddenFromNonAdmins}
            onChange={e => setHiddenFromNonAdmins(!hiddenFromNonAdmins)}
          />
        )}
        <div className="flex gap-2 mt-6">
          <Button type="submit" variant="primary" isDisabled={saving || Object.values(formValid).some(v => !v)}>{L.user.save}</Button>
          <Button type="button" variant="secondary" onClick={handleCancel}>{L.user.cancel}</Button>
          <Button type="button" variant="danger" onClick={handleDelete}>{L.user.delete_user}</Button>
        </div>
      </form> 
        :
        <div className="space-y-4">
          <div>
            <div className="text-lg text-gray-500">{L.user.name}</div>
            <div className="text-base font-medium">{user?.name}</div>
          </div>
          <div>
            <div className="text-lg text-gray-500">{L.user.email}</div>
            <div className="text-base font-medium">{user?.email}</div>
          </div>
          <div>
            <div className="text-lg text-gray-500">{L.user.phone}</div>
            <div className="text-base font-medium">{user?.phone}</div>
          </div>
          <div>
            <div className="text-lg text-gray-500">{L.user.organization}</div>
            <div className="text-base font-medium">{user?.organization_name}</div>
          </div>
          <div>
            <div className="text-lg text-gray-500">{L.user.user_group}</div>
            <div className="text-base font-medium">{user?.user_group_name}</div>
          </div>
          {isAdmin && (
            <div>
              <div className="text-lg text-gray-500">{L.user.hidden}</div>
              <div className="text-base font-medium">{user && isUserHiddenFromNonAdmins(user) ? 'Yes' : 'No'}</div>
            </div>
          )}
          <div className="pt-4">
            { canEdit ? 
              <Button type="button" variant="secondary" onClick={() => setEditing(true)}>
                {L.user.edit_user}
              </Button> 
              : 
              <Tooltip text={user?.source !== 'User Management' ? L.user.edit_user_disabled : L.user.edit_user_admin_only} asChild={true}>
                <Button type="button" variant="secondary" isDisabled={true}>
                  {L.user.edit_user}
                </Button>
              </Tooltip> 
            }
          </div>
        </div>}
    </Card>
  </div>
}

export default function UserPage() {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const getNav = usePost("/navigation");

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (Array.isArray(result?.navigation)) {
      setNav(result.navigation);
    }
  }, []);
  
  return (
    <ThreeColumnPage
      nav={nav}
      title="User Management"
      main={<UserPageInner />}
      isDashboard={true}
    />
  );
}
